import React from 'react';
import { Box, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';

const MobilePreview = () => {
    const { control } = useFormContext();

    const title = useWatch({ control, name: 'title' });
    const subtitle = useWatch({ control, name: 'subtitle' });
    const description = useWatch({ control, name: 'description' });
    const imageUrl = useWatch({ control, name: 'imageUrl' });
    const thumbnailUrl = useWatch({ control, name: 'thumbnailUrl' });
    const aspectRatio = useWatch({ control, name: 'aspectRatio' });
    const badgeText = useWatch({ control, name: 'badgeText' });
    const badgeColor = useWatch({ control, name: 'badgeColor' });
    const badgeTextColor = useWatch({ control, name: 'badgeTextColor' });
    const badgePosition = useWatch({ control, name: 'badgePosition' });
    const cardBackgroundColor = useWatch({ control, name: 'cardBackgroundColor' });
    const cardBorderColor = useWatch({ control, name: 'cardBorderColor' });
    const displayOptions = useWatch({ control, name: 'displayOptions' }) || {};

    const {
        showTitle, titlePosition, showSubTitle, subTitlePosition,
        showDescription, descriptionPosition,
        titleColor, titleFontFamily, titleFontSize,
        titleFontWeight, titleFontItalic,
        subTitleColor, subTitleFontFamily, subTitleFontSize, subTitleFontWeight, subTitleFontItalic,
        descriptionColor, descriptionFontFamily, descriptionFontSize, descriptionFontWeight, descriptionFontItalic,
        cardWidth, cardHeight,
        marginTop, marginRight, marginBottom, marginLeft,
        paddingTop, paddingRight, paddingBottom, paddingLeft,
    } = displayOptions;

    const toAlign = (pos) => {
        if (pos === 'center') return 'center';
        if (pos === 'right') return 'flex-end';
        return 'flex-start';
    };

    const toTextAlign = (pos) => {
        if (pos === 'center') return 'center';
        if (pos === 'right') return 'right';
        return 'left';
    };

    const badgePos = {
        'top-left':     { top: 6, left: 6 },
        'top-right':    { top: 6, right: 6 },
        'bottom-left':  { bottom: 6, left: 6 },
        'bottom-right': { bottom: 6, right: 6 },
    }[badgePosition] || { top: 6, right: 6 };

    const previewImage = thumbnailUrl || imageUrl;
    const imageFit = displayOptions.imageFit || displayOptions.objectFit || 'contain';
    const previewAspectRatio = aspectRatio && aspectRatio !== 'auto'
        ? aspectRatio.replace(':', ' / ')
        : '1 / 1';

    const cardW = cardWidth ? `${cardWidth}px` : '140px';
    const cardH = cardHeight ? `${cardHeight}px` : '180px';

    return (
        <Box sx={{ mt: 2 }}>
            {/* Phone shell */}
            <Box sx={{
                width: 280,
                mx: 'auto',
                borderRadius: '36px',
                border: '8px solid #222',
                backgroundColor: '#f5f5f5',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                overflow: 'hidden',
                position: 'relative',
            }}>
                {/* Notch */}
                <Box sx={{
                    width: 80, height: 22, bgcolor: '#222',
                    borderRadius: '0 0 14px 14px',
                    mx: 'auto', mb: 0
                }} />

                {/* Screen */}
                <Box sx={{
                    bgcolor: '#fff',
                    minHeight: 420,
                    px: 1.5, py: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                    <Typography sx={{ fontSize: '0.65rem', color: '#aaa', mb: 1, alignSelf: 'flex-start' }}>
                        Home Screen Preview
                    </Typography>

                    {/* Item Card */}
                    <Box sx={{
                        width: cardW,
                        minHeight: cardH,
                        backgroundColor: cardBackgroundColor || '#fff',
                        border: `1px solid ${cardBorderColor || '#e0e0e0'}`,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        marginTop: marginTop ? `${marginTop}px` : '4px',
                        marginRight: marginRight ? `${marginRight}px` : '4px',
                        marginBottom: marginBottom ? `${marginBottom}px` : '4px',
                        marginLeft: marginLeft ? `${marginLeft}px` : '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}>
                        {/* Media */}
                        {previewImage ? (
                            <Box
                                component="img"
                                src={previewImage}
                                alt="preview"
                                sx={{
                                    width: '100%',
                                    aspectRatio: previewAspectRatio,
                                    height: 'auto',
                                    maxHeight: '180px',
                                    objectFit: imageFit,
                                    display: 'block',
                                    flexShrink: 0,
                                }}
                            />
                        ) : (
                            <Box sx={{
                                width: '100%',
                                aspectRatio: previewAspectRatio,
                                minHeight: '100px',
                                bgcolor: '#e8e8e8',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                <Typography sx={{ fontSize: '0.6rem', color: '#aaa' }}>No Media</Typography>
                            </Box>
                        )}

                        {/* Badge */}
                        {badgeText && (
                            <Box sx={{
                                position: 'absolute',
                                ...badgePos,
                                backgroundColor: badgeColor || '#FF6B35',
                                color: badgeTextColor || '#FFFFFF',
                                fontSize: '0.5rem',
                                fontWeight: 700,
                                px: 0.6,
                                py: 0.2,
                                borderRadius: '4px',
                            }}>
                                {badgeText}
                            </Box>
                        )}

                        {/* Text content */}
                        <Box sx={{
                            px: paddingLeft ? `${paddingLeft}px` : '8px',
                            py: paddingTop ? `${paddingTop}px` : '6px',
                            paddingRight: paddingRight ? `${paddingRight}px` : '8px',
                            paddingBottom: paddingBottom ? `${paddingBottom}px` : '6px',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                        }}>
                            {showTitle && title && (
                                <Box sx={{ display: 'flex', justifyContent: toAlign(titlePosition) }}>
                                    <Typography sx={{
                                        fontSize: titleFontSize ? `${Math.min(titleFontSize, 18)}px` : '0.72rem',
                                        fontWeight: titleFontWeight || 600,
                                        fontStyle: titleFontItalic ? 'italic' : 'normal',
                                        color: titleColor || '#222',
                                        fontFamily: titleFontFamily && titleFontFamily !== 'Default' ? titleFontFamily : 'inherit',
                                        textAlign: toTextAlign(titlePosition),
                                        lineHeight: 1.2,
                                        wordBreak: 'break-word',
                                    }}>
                                        {title}
                                    </Typography>
                                </Box>
                            )}
                            {showSubTitle && subtitle && (
                                <Box sx={{ display: 'flex', justifyContent: toAlign(subTitlePosition) }}>
                                    <Typography sx={{
                                        fontSize: subTitleFontSize ? `${Math.min(subTitleFontSize, 14)}px` : '0.62rem',
                                        color: subTitleColor || '#555',
                                        fontWeight: subTitleFontWeight || 400,
                                        fontStyle: subTitleFontItalic ? 'italic' : 'normal',
                                        fontFamily: subTitleFontFamily && subTitleFontFamily !== 'Default' ? subTitleFontFamily : 'inherit',
                                        textAlign: toTextAlign(subTitlePosition),
                                        lineHeight: 1.2,
                                        wordBreak: 'break-word',
                                    }}>
                                        {subtitle}
                                    </Typography>
                                </Box>
                            )}
                            {showDescription && description && (
                                <Box sx={{ display: 'flex', justifyContent: toAlign(descriptionPosition) }}>
                                    <Typography sx={{
                                        fontSize: descriptionFontSize ? `${Math.min(descriptionFontSize, 12)}px` : '0.58rem',
                                        color: descriptionColor || '#777',
                                        fontWeight: descriptionFontWeight || 400,
                                        fontStyle: descriptionFontItalic ? 'italic' : 'normal',
                                        fontFamily: descriptionFontFamily && descriptionFontFamily !== 'Default' ? descriptionFontFamily : 'inherit',
                                        textAlign: toTextAlign(descriptionPosition),
                                        lineHeight: 1.3,
                                        wordBreak: 'break-word',
                                    }}>
                                        {description}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Hint */}
                    <Typography sx={{ fontSize: '0.58rem', color: '#bbb', mt: 1.5, textAlign: 'center' }}>
                        Live preview — updates as you type
                    </Typography>
                </Box>

                {/* Home bar */}
                <Box sx={{
                    height: 20, bgcolor: '#f5f5f5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <Box sx={{ width: 60, height: 4, bgcolor: '#ccc', borderRadius: 2 }} />
                </Box>
            </Box>
        </Box>
    );
};

export default MobilePreview;
